import React from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PreviewModal = ({ mood, onClose, onSelect }) => {
  if (!mood) return null;

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Easy':
        return 'text-accent border-accent';
      case 'Medium':
        return 'text-warning border-warning';
      case 'Hard':
        return 'text-error border-error';
      case 'Extreme':
        return 'text-secondary border-secondary';
      default:
        return 'text-primary border-primary';
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-background/90 backdrop-blur-sm">
      <div className="w-full max-w-2xl bg-card border-2 border-primary rounded-sm shadow-glow-cyan-lg max-h-[90vh] overflow-y-auto terminal-scrollbar">
        <div className="sticky top-0 bg-card border-b-2 border-primary/30 p-4 md:p-6 flex items-center justify-between z-10">
          <h2 className="font-heading font-bold text-xl md:text-2xl text-primary">AI Mood Preview</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-sm border border-primary/50 hover:border-primary hover:bg-primary/10 transition-all duration-base touch-target"
          >
            <Icon name="X" size={20} color="var(--color-primary)" />
          </button>
        </div>

        <div className="p-4 md:p-6 space-y-4 md:space-y-6">
          <div className="flex flex-col md:flex-row items-start gap-4 md:gap-6">
            <div className="w-24 h-24 md:w-32 md:h-32 flex-shrink-0 bg-background border-2 border-primary rounded-sm overflow-hidden mx-auto md:mx-0">
              <Image
                src={mood?.avatar}
                alt={mood?.avatarAlt}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1 text-center md:text-left">
              <h3 className="font-heading font-bold text-2xl md:text-3xl text-primary mb-2">
                {mood?.name}
              </h3>
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 border rounded-sm ${getDifficultyColor(mood?.difficulty)} mb-3`}>
                <Icon name="Zap" size={18} />
                <span className="text-sm font-caption font-semibold">{mood?.difficulty} Difficulty</span>
              </div>
              <p className="text-sm md:text-base text-muted-foreground font-caption">
                {mood?.description}
              </p>
            </div>
          </div>

          <div className="space-y-3 md:space-y-4">
            <div className="p-4 bg-background border border-secondary/30 rounded-sm">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Brain" size={20} color="var(--color-secondary)" />
                <h4 className="font-heading font-semibold text-sm md:text-base text-secondary">Personality Traits</h4>
              </div>
              <p className="text-sm md:text-base text-foreground font-caption">
                {mood?.personality}
              </p>
            </div>

            <div className="p-4 bg-background border border-accent/30 rounded-sm">
              <div className="flex items-center gap-2 mb-3">
                <Icon name="MessageSquare" size={20} color="var(--color-accent)" />
                <h4 className="font-heading font-semibold text-sm md:text-base text-accent">Dialogue Samples</h4>
              </div>
              <div className="space-y-2">
                {mood?.dialogueSamples?.map((dialogue, index) => (
                  <div key={index} className="p-3 bg-card border border-primary/20 rounded-sm">
                    <p className="text-xs md:text-sm text-accent font-data italic">
                      "{dialogue}"
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-4 bg-background border border-warning/30 rounded-sm">
              <div className="flex items-center gap-2 mb-3">
                <Icon name="Target" size={20} color="var(--color-warning)" />
                <h4 className="font-heading font-semibold text-sm md:text-base text-warning">Tactics & Strategy</h4>
              </div>
              <ul className="space-y-2">
                {mood?.tactics?.map((tactic, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm md:text-base text-foreground font-caption">
                    <Icon name="ChevronRight" size={16} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
                    <span>{tactic}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="p-4 bg-background border border-error/30 rounded-sm">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="AlertTriangle" size={20} color="var(--color-error)" />
                <h4 className="font-heading font-semibold text-sm md:text-base text-error">Warning</h4>
              </div>
              <p className="text-sm md:text-base text-foreground font-caption">
                {mood?.warning}
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-primary/30">
            <Button
              variant="default"
              size="lg"
              iconName="Play"
              iconPosition="left"
              onClick={() => {
                onSelect(mood?.id);
                onClose();
              }}
              fullWidth
            >
              Select This Mood
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={onClose}
              fullWidth
            >
              Close Preview
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewModal;